import axiosClient from './axios-client';
import { API_ENDPOINTS } from '@/constants';
import type { Order } from './orders';
import type { User } from './users';

/** Kanban column keys — matches WarehouseStatusEnum in gt-engine-be src/modules/orders/domain/enums/order.enum.ts */
export enum WarehouseOrderStatus {
  AWAITING = 'awaiting',
  ALMOST_OPEN = 'almost_open',
  OPEN_ORDERS = 'open_orders',
  LABEL_AWAITING = 'label_awaiting',
  SHIPMENT_WILL_CALL = 'shipment_will_call',
  ISSUE = 'issue',
  COMPLETED = 'completed',
}

/**
 * Which side of the two-way tracking comparison an entry belongs to — matches
 * TrackingSourceEnum in gt-engine-be's inbound-tracking.entity.ts. Sales:
 * the salesperson's manually-entered number (from the supplier). Warehouse:
 * the handler's scanned number.
 */
export enum TrackingSourceEnum {
  SALES = 'sales',
  WAREHOUSE = 'warehouse',
}

export enum PackageType {
  BOX = 'box',
  PALLET = 'pallet',
}

export enum OrderClassification {
  DOMESTIC = 'domestic',
  INTERNATIONAL = 'international',
}

export enum InboundTrackingType {
  PARCEL = 'parcel',
  LOCAL_PICKUP = 'local_pickup',
}

export interface WarehousePersonRef {
  id: string;
  name: string;
}

export interface InboundTrackingEntry {
  id: string;
  orderId: string;
  trackingType: InboundTrackingType;
  courierOrSupplierName: string;
  trackingNumber?: string | null;
  status?: string | null;
  note?: string | null;
  source: TrackingSourceEnum;
  attributedToId?: string | null;
  attributedTo?: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
  updatedAt: string;
}

/** Returned only from add/update — true when this warehouse-scanned entry currently matches a sales entry; absent for sales-source entries. */
export interface InboundTrackingMutationResult extends InboundTrackingEntry {
  isMatch?: boolean;
}

/** One card on the Kanban board — GET /warehouse/kanban */
export interface WarehouseCard {
  id: string;
  orderNumber: string;
  /** null when the order has no invoice yet — comes from order.invoices[0]?.invoiceNumber, a separate Invoice entity */
  invoiceNumber: string | null;
  createdAt: string;
  packageType: PackageType | null;
  orderClassification: OrderClassification | null;
  destinationCountry: string | null;
  assignedSalesperson: WarehousePersonRef | null;
  assignedWarehouseHandler: WarehousePersonRef | null;
  inboundTrackingSummary: InboundTrackingEntry[];
}

/** Response of GET /warehouse/kanban — only columns visible to the caller's role are present. */
export type WarehouseKanbanBoard = Partial<Record<WarehouseOrderStatus, WarehouseCard[]>>;

/** A warehouse note lives in Order.metadata.warehouseNotes (jsonb) — no dedicated table/endpoint. */
export interface WarehouseNote {
  id: string;
  text: string;
  /** userId of the author — the backend doesn't resolve this to a name */
  author: string;
  createdAt: string;
}

/** A file attached via POST /warehouse/orders/:id/files — lives in Order.metadata.warehouseFiles (jsonb). */
export interface WarehouseFile {
  id: string;
  url: string;
  key: string;
  name: string;
  uploadedBy: string;
  uploadedAt: string;
}

/** One entry of the real order history — backed by the shared order_status_logs table. */
export interface OrderStatusLogEntry {
  id: string;
  statusLabel: string;
  /** Resolved server-side to a display name (or email/source as fallback) — never a raw id. */
  performedBy: string | null;
  createdAt: string;
}

/** A recorded packed box — POST /warehouse/orders/:id/packages, backed by the order_packages table. Standalone record, not linked to specific order items. */
export interface WarehousePackage {
  id: string;
  weight: string;
  largestDimension: string;
  middleDimension: string;
  smallestDimension: string;
  note: string | null;
  createdAt: string;
}

/** Fixed 3-slot document set — matches PaymentMetadata.shippingDocuments key names. */
export type ShippingDocumentType = 'invoice' | 'label' | 'bill_of_lading';

export interface ShippingDocument {
  url: string;
  key: string;
  name: string;
  uploadedBy: string;
  uploadedAt: string;
}

export interface WarehouseOrderDetailResponse {
  /** Relations loaded: items, shippingAddress, billingAddress, customer, transactions, assignedSalesperson, childOrders.
   *  NOT loaded here: assignedWarehouseHandler (only present on the Kanban card). */
  order: Order;
  inboundTracking: InboundTrackingEntry[];
  packages: WarehousePackage[];
  logs: OrderStatusLogEntry[];
}

/** Replaces the deprecated PATCH .../handler — both fields independently optional. */
export interface UpdateAssignmentRequest {
  handlerId?: string;
  salespersonId?: string;
}

export interface VerifyItemRequest {
  id: string;
  isVerified?: boolean;
  missedItemsQty?: number;
  /** Free-text column server-side (no enum) — the UI constrains it to Pending/In Stock/Out of Stock. */
  vendorStatus?: string;
}

export interface VerifyItemsRequest {
  items: VerifyItemRequest[];
}

export interface ReportIssueRequest {
  text: string;
}

export interface AddWarehouseNoteRequest {
  text: string;
}

export interface AddInboundTrackingRequest {
  courierOrSupplierName: string;
  trackingNumber?: string;
  note?: string;
  /** Defaults to the acting user server-side if omitted. */
  attributedToId?: string;
  /** Only meaningful (and required) for an admin acting on behalf of another role — inferred server-side for sales/handler callers. */
  source?: TrackingSourceEnum;
}

/** All fields independently optional — PATCH .../inbound/:trackingId */
export interface UpdateInboundTrackingRequest {
  courierOrSupplierName?: string;
  trackingNumber?: string;
  note?: string;
  attributedToId?: string;
}

export interface NotifyCustomerRequest {
  subject: string;
  message: string;
}

export interface AddPackageRequest {
  weight: number;
  largestDimension: number;
  middleDimension: number;
  smallestDimension: number;
  note?: string;
}

export const warehouseApi = {
  getKanban: async (): Promise<WarehouseKanbanBoard> => {
    const response = await axiosClient.get(API_ENDPOINTS.WAREHOUSE_KANBAN);
    return response.data;
  },

  /** Real dedicated endpoint — filters by the actual UserEntity.isWarehouseHandler flag (staff only). */
  getHandlers: async (): Promise<User[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.WAREHOUSE_HANDLERS);
    return response.data;
  },

  /** Powers both the quick-view drawer (US03) and the full detail page (US04) — same endpoint. */
  getOrderDetail: async (orderId: string): Promise<WarehouseOrderDetailResponse> => {
    const response = await axiosClient.get(API_ENDPOINTS.WAREHOUSE_ORDER_BY_ID(orderId));
    return response.data;
  },

  /** Returned Order is missing assignedWarehouseHandler/invoices relations — invalidate rather than cache-patch from this response. */
  updateAssignment: async (orderId: string, data: UpdateAssignmentRequest): Promise<Order> => {
    const response = await axiosClient.patch(API_ENDPOINTS.WAREHOUSE_ASSIGNMENT(orderId), data);
    return response.data;
  },

  attachFiles: async (orderId: string, files: File[]): Promise<Order> => {
    const form = new FormData();
    files.forEach((file) => form.append('files', file));
    const response = await axiosClient.post(API_ENDPOINTS.WAREHOUSE_FILES(orderId), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteFile: async (orderId: string, fileId: string): Promise<Order> => {
    const response = await axiosClient.delete(API_ENDPOINTS.WAREHOUSE_FILE_BY_ID(orderId, fileId));
    return response.data;
  },

  verifyItems: async (orderId: string, data: VerifyItemsRequest): Promise<Order> => {
    const response = await axiosClient.patch(API_ENDPOINTS.WAREHOUSE_VERIFY_ITEMS(orderId), data);
    return response.data;
  },

  /** Adds a note prefixed "Issue Reported: " to Order.metadata.warehouseNotes and moves the order to ISSUE. */
  reportIssue: async (orderId: string, data: ReportIssueRequest): Promise<Order> => {
    const response = await axiosClient.post(API_ENDPOINTS.WAREHOUSE_REPORT_ISSUE(orderId), data);
    return response.data;
  },

  addNote: async (orderId: string, data: AddWarehouseNoteRequest): Promise<Order> => {
    const response = await axiosClient.post(API_ENDPOINTS.WAREHOUSE_NOTES(orderId), data);
    return response.data;
  },

  /** Order moves AWAITING -> ALMOST_OPEN -> OPEN_ORDERS as sales/warehouse tracking numbers match up. */
  addInboundTracking: async (orderId: string, data: AddInboundTrackingRequest): Promise<InboundTrackingMutationResult> => {
    const response = await axiosClient.post(API_ENDPOINTS.WAREHOUSE_INBOUND(orderId), data);
    return response.data;
  },

  updateInboundTracking: async (
    orderId: string,
    trackingId: string,
    data: UpdateInboundTrackingRequest
  ): Promise<InboundTrackingMutationResult> => {
    const response = await axiosClient.patch(API_ENDPOINTS.WAREHOUSE_INBOUND_BY_ID(orderId, trackingId), data);
    return response.data;
  },

  deleteInboundTracking: async (orderId: string, trackingId: string): Promise<void> => {
    await axiosClient.delete(API_ENDPOINTS.WAREHOUSE_INBOUND_BY_ID(orderId, trackingId));
  },

  /** Sends a real email (via Brevo) to order.customer.email — throws if the customer has no email on file. */
  notifyCustomer: async (orderId: string, data: NotifyCustomerRequest): Promise<void> => {
    await axiosClient.post(API_ENDPOINTS.WAREHOUSE_NOTIFY(orderId), data);
  },

  addPackage: async (orderId: string, data: AddPackageRequest): Promise<WarehousePackage> => {
    const response = await axiosClient.post(API_ENDPOINTS.WAREHOUSE_PACKAGES(orderId), data);
    return response.data;
  },

  uploadShippingDocument: async (orderId: string, type: ShippingDocumentType, file: File): Promise<Order> => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosClient.post(API_ENDPOINTS.WAREHOUSE_SHIPPING_DOCUMENT(orderId, type), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteShippingDocument: async (orderId: string, type: ShippingDocumentType): Promise<Order> => {
    const response = await axiosClient.delete(API_ENDPOINTS.WAREHOUSE_SHIPPING_DOCUMENT(orderId, type));
    return response.data;
  },

  markReadyForShipping: async (orderId: string): Promise<Order> => {
    const response = await axiosClient.post(API_ENDPOINTS.WAREHOUSE_READY_FOR_SHIPPING(orderId));
    return response.data;
  },
};

export function isInternationalOrder(country: string | null | undefined): boolean {
  return !!country && country.toUpperCase() !== 'US' && country.toUpperCase() !== 'USA';
}
